import React, { useState, useEffect, useMemo } from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { colors, typography, spacing } from '../../theme';
import { useAsyncAnalysis } from '../../hooks/useAsyncAnalysis';
import { validateText } from '../../utils/text-validation';
import { validateFile } from '../../utils/file-validation';
import type { AnalysisSession } from '../../types/analysis-session';
import { InputPreview } from '../PipelineAnalysis/sections/InputPreview';
import { AiResults, type AiDisplayResult, toAiDisplayResult } from '../PipelineAnalysis/sections/AiResults';
import { VerdictSection } from '../PipelineAnalysis/styles';

// =============================================================================
// Input Types & Helpers
// =============================================================================

type InputType = 'text' | 'image' | 'audio' | 'video';
type AnalysisMode = 'quick' | 'full';

const INPUT_TYPES: { key: InputType; labelKey: string; accept?: string }[] = [
  { key: 'text', labelKey: 'common.text' },
  { key: 'image', labelKey: 'common.image', accept: 'image/jpeg,image/png,image/webp,image/gif' },
  { key: 'audio', labelKey: 'common.audio', accept: 'audio/mpeg,audio/wav,audio/ogg,audio/mp4' },
  { key: 'video', labelKey: 'common.video', accept: 'video/mp4,video/webm,video/ogg' },
];

// =============================================================================
// Component
// =============================================================================

export const AiTamper: React.FC = () => {
  const { t } = useTranslation();
  const async = useAsyncAnalysis('ai-tampered');
  const [inputType, setInputType] = useState<InputType>('text');
  const [inputText, setInputText] = useState('');
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [analysisMode, setAnalysisMode] = useState<AnalysisMode>('quick');
  const [displayResult, setDisplayResult] = useState<AiDisplayResult | null>(null);
  const [analyzedInput, setAnalyzedInput] = useState<{ type: InputType; text: string } | null>(null);
  const fileInputRef = React.useRef<HTMLInputElement>(null);

  // Parse async result (AnalysisSession) into AiDisplayResult when it arrives
  useEffect(() => {
    if (!async.result) return;
    try {
      const session: AnalysisSession = async.result;
      const r = session.ai_tampered;
      if (!r) return;

      const parsed = toAiDisplayResult(r, {
        inputType: session.input_type,
        inputText: session.input_text,
      });

      // For audio/video, swap the input preview snapshot to the transcript text
      if (session.input_text && (session.input_type === 'audio' || session.input_type === 'video')) {
        setAnalyzedInput(prev => prev ? { ...prev, text: session.input_text! } : prev);
      }

      setDisplayResult(parsed);
    } catch {
      // result parsing failed
    }
  }, [async.result]);

  const [fileError, setFileError] = useState<string | null>(null);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const validation = validateFile(file, inputType);
    if (!validation.valid) {
      setFileError(validation.error);
      if (fileInputRef.current) fileInputRef.current.value = '';
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  };

  const handleRemoveFile = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleTypeChange = (type: InputType) => {
    setInputType(type);
    setSelectedFile(null);
    setDisplayResult(null);
    setAnalyzedInput(null);
    async.reset();
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const textValidation = useMemo(() => validateText(inputText), [inputText]);

  const canAnalyze =
    inputType === 'text' ? textValidation.valid : selectedFile !== null;

  const handleAnalyze = () => {
    if (!canAnalyze) return;
    setDisplayResult(null);

    if (inputType === 'text') {
      const txt = inputText.trim();
      setAnalyzedInput({ type: inputType, text: txt });
      async.submitText(txt, { mode: analysisMode });
    } else if (selectedFile) {
      setAnalyzedInput({ type: inputType, text: selectedFile.name });
      async.submitMedia(selectedFile, inputType);
    }
  };

  // Aliases for template compatibility
  const isAnalyzing = async.isAnalyzing;
  const error = async.error;

  return (
    <Container>
      {/* Header */}
      <Header>
        <Title>{t('aiTamper.title')}</Title>
        <Subtitle>{t('aiTamper.subtitle')}</Subtitle>
      </Header>

      {/* Input Type Selector */}
      <TypeSelector>
        {INPUT_TYPES.map(({ key, labelKey }) => (
          <TypeButton
            key={key}
            active={inputType === key}
            onClick={() => handleTypeChange(key)}
          >
            {t(labelKey)}
          </TypeButton>
        ))}
      </TypeSelector>

      {/* Analysis Mode Toggle — text only */}
      {inputType === 'text' && (
        <ModeSelector>
          <ModeButton
            active={analysisMode === 'quick'}
            onClick={() => setAnalysisMode('quick')}
          >
            <ModeLabel>{t('aiTamper.quickScan')}</ModeLabel>
            <ModeDesc>{t('aiTamper.quickScanDesc')}</ModeDesc>
          </ModeButton>
          <ModeButton
            active={analysisMode === 'full'}
            onClick={() => setAnalysisMode('full')}
          >
            <ModeLabel>{t('aiTamper.deepAnalysis')}</ModeLabel>
            <ModeDesc>{t('aiTamper.deepAnalysisDesc')}</ModeDesc>
          </ModeButton>
        </ModeSelector>
      )}

      {/* Input Area */}
      <InputArea>
        {inputType === 'text' ? (
          <TextInput
            placeholder={t('aiTamper.textPlaceholder')}
            value={inputText}
            onChange={(e) => setInputText(e.target.value)}
            disabled={isAnalyzing}
          />
        ) : (
          <FileDropZone>
            <input
              ref={fileInputRef}
              type="file"
              accept={INPUT_TYPES.find(t => t.key === inputType)?.accept}
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
            {selectedFile ? (
              <FileSelected>
                <FileName>{selectedFile.name}</FileName>
                <FileSize>{(selectedFile.size / (1024 * 1024)).toFixed(1)} MB</FileSize>
                <RemoveFileBtn onClick={handleRemoveFile}>Remove</RemoveFileBtn>
              </FileSelected>
            ) : (
              <DropPlaceholder onClick={() => fileInputRef.current?.click()}>
                <DropLabel>Click to select {inputType} file</DropLabel>
                <DropHint>
                  {inputType === 'image' && t('pipeline.imageHint')}
                  {inputType === 'audio' && t('pipeline.audioHint')}
                  {inputType === 'video' && t('pipeline.videoHint')}
                </DropHint>
              </DropPlaceholder>
            )}
            {fileError && <FileErrorMsg>{fileError}</FileErrorMsg>}
          </FileDropZone>
        )}

        <InputFooter>
          <CharCount style={inputType === 'text' ? { color: textValidation.level === 'error' ? '#ef4444' : textValidation.level === 'warning' ? '#eab308' : '#22c55e' } : undefined}>
            {inputType === 'text'
              ? (textValidation.error || textValidation.warning || `${inputText.trim().length} characters`)
              : selectedFile ? t('common.readyToAnalyze') : t('common.noFileSelected')}
          </CharCount>
          <AnalyzeBtn
            onClick={handleAnalyze}
            disabled={isAnalyzing || !canAnalyze}
          >
            {isAnalyzing ? (
              <>
                <Spinner />
                {async.statusText || (inputType === 'text' && analysisMode === 'quick' ? t('aiTamper.scanning') : t('common.analyzing'))}
              </>
            ) : (
              inputType === 'text'
                ? (analysisMode === 'quick' ? t('aiTamper.quickScan') : t('aiTamper.deepAnalyze'))
                : t('common.analyze')
            )}
          </AnalyzeBtn>
        </InputFooter>
      </InputArea>

      {/* Error */}
      {error && (
        <ErrorBox>
          <ErrorIcon>!</ErrorIcon>
          {error}
        </ErrorBox>
      )}

      {/* Backend Warnings */}
      {async.warnings.length > 0 && (
        <WarningBox>
          {async.warnings.map((w, i) => <div key={i}>{w}</div>)}
        </WarningBox>
      )}

      {/* Input Preview (shown once result lands) */}
      {displayResult && analyzedInput && (
        <InputPreview inputType={analyzedInput.type} text={analyzedInput.text} />
      )}

      {/* Results — editorial layout */}
      {displayResult && (
        <VerdictSection>
          <AiResults
            result={displayResult}
            onTryDeepScan={
              inputType === 'text' && analysisMode === 'quick'
                ? () => { setAnalysisMode('full'); setDisplayResult(null); }
                : undefined
            }
          />
        </VerdictSection>
      )}
    </Container>
  );
};

// =============================================================================
// Styled Components — input form & banners (results use shared editorial)
// =============================================================================

const Container = styled.div`
  width: 100%;
  max-width: 1800px;
  margin: 0 auto;
  padding: 0;
  @media (max-width: 768px) { padding: 0; }
`;

const Header = styled.div`
  margin-bottom: ${spacing.xl}px;
`;

const Title = styled.h1`
  font-family: ${typography.fontFamily.primary};
  font-size: 28px;
  font-weight: ${typography.fontWeight.bold};
  color: #ffffff;
  margin: 0 0 ${spacing.xs}px 0;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const Subtitle = styled.p`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  color: rgba(255, 255, 255, 0.5);
  margin: 0;

  [data-theme="light"] & {
    color: ${colors.steelGray};
  }
`;

/* Input Type Selector */
const TypeSelector = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px;
  background: rgba(255, 255, 255, 0.04);
  border-radius: 12px;
  margin-bottom: ${spacing.md}px;
  border: 1px solid rgba(255, 255, 255, 0.06);

  [data-theme="light"] & {
    background: ${colors.warmGray};
    border-color: ${colors.lightGray};
  }
`;

const TypeButton = styled.button<{ active?: boolean; disabled?: boolean }>`
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 16px;
  border: none;
  border-radius: 8px;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${props => props.active ? typography.fontWeight.semibold : typography.fontWeight.medium};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;

  background: ${props => props.active ? 'rgba(139, 92, 246, 0.2)' : 'transparent'};
  color: ${props => props.active
    ? '#a78bfa'
    : props.disabled ? 'rgba(255, 255, 255, 0.2)' : 'rgba(255, 255, 255, 0.6)'};

  ${props => props.active && `box-shadow: 0 0 0 1px rgba(139, 92, 246, 0.3);`}

  [data-theme="light"] & {
    background: ${props => props.active ? 'white' : 'transparent'};
    color: ${props => props.active
      ? colors.neonViolet
      : props.disabled ? colors.hintGray : colors.steelGray};
    ${props => props.active && `box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);`}
  }

  &:hover:not(:disabled) {
    background: ${props => props.active ? 'rgba(139, 92, 246, 0.2)' : 'rgba(255, 255, 255, 0.06)'};
  }

  [data-theme="light"] &:hover:not(:disabled) {
    background: ${props => props.active ? 'white' : 'rgba(0, 0, 0, 0.03)'};
  }
`;

/* Analysis Mode Selector */
const ModeSelector = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
  margin-bottom: ${spacing.lg}px;

  @media (max-width: 480px) {
    grid-template-columns: 1fr;
  }
`;

const ModeButton = styled.button<{ active?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  padding: 14px 16px;
  border: 1px solid ${props => props.active
    ? 'rgba(139, 92, 246, 0.4)' : 'rgba(255, 255, 255, 0.06)'};
  border-radius: 10px;
  background: ${props => props.active
    ? 'rgba(139, 92, 246, 0.08)' : 'rgba(255, 255, 255, 0.02)'};
  cursor: pointer;
  transition: all 0.2s;

  [data-theme="light"] & {
    border-color: ${props => props.active ? colors.neonViolet : colors.lightGray};
    background: ${props => props.active ? 'rgba(139, 92, 246, 0.05)' : 'white'};
  }

  &:hover {
    border-color: rgba(139, 92, 246, 0.3);
  }
`;

const ModeLabel = styled.span`
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  color: #ffffff;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const ModeDesc = styled.span`
  font-size: 11px;
  color: rgba(255, 255, 255, 0.35);

  [data-theme="light"] & {
    color: ${colors.hintGray};
  }
`;

/* Input Area */
const InputArea = styled.div`
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 14px;
  overflow: hidden;
  margin-bottom: ${spacing.lg}px;
  transition: border-color 0.2s;

  &:focus-within {
    border-color: rgba(139, 92, 246, 0.4);
  }

  [data-theme="light"] & {
    background: white;
    border-color: ${colors.lightGray};
    &:focus-within { border-color: ${colors.neonViolet}; }
  }
`;

const TextInput = styled.textarea`
  width: 100%;
  min-height: 160px;
  padding: ${spacing.lg}px;
  background: transparent;
  border: none;
  font-family: ${typography.fontFamily.primary};
  font-size: 15px;
  color: #ffffff;
  resize: vertical;
  box-sizing: border-box;
  line-height: 1.6;

  &:focus { outline: none; }
  &::placeholder { color: rgba(255, 255, 255, 0.25); }

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
    &::placeholder { color: ${colors.hintGray}; }
  }
`;

const InputFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${spacing.sm}px ${spacing.lg}px;
  border-top: 1px solid rgba(255, 255, 255, 0.06);

  [data-theme="light"] & {
    border-top-color: ${colors.lightGray};
  }

  @media (max-width: 480px) {
    flex-direction: column;
    gap: 8px;
    padding: ${spacing.md}px ${spacing.lg}px;
  }
`;

const CharCount = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);
  [data-theme="light"] & { color: ${colors.hintGray}; }
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const Spinner = styled.span`
  display: inline-block;
  width: 14px;
  height: 14px;
  border: 2px solid var(--accent-subtle);
  border-top-color: white;
  border-radius: 50%;
  animation: ${spin} 0.7s linear infinite;
`;

const AnalyzeBtn = styled.button<{ disabled?: boolean }>`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 24px;
  background: ${props => props.disabled
    ? 'rgba(139, 92, 246, 0.15)'
    : 'linear-gradient(135deg, #8b5cf6, #7c3aed)'};
  color: ${props => props.disabled ? 'var(--fg-disabled)' : 'var(--fg-on-accent)'};
  border: none;
  border-radius: 8px;
  font-family: ${typography.fontFamily.primary};
  font-size: ${typography.fontSize.sm};
  font-weight: ${typography.fontWeight.semibold};
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 4px 16px rgba(139, 92, 246, 0.35);
  }

  @media (max-width: 480px) {
    width: 100%;
    justify-content: center;
  }
`;

/* File Upload */
const FileDropZone = styled.div`
  padding: ${spacing.lg}px;
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const DropPlaceholder = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 40px;
  border: 2px dashed rgba(139, 92, 246, 0.2);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
  width: 100%;

  &:hover {
    border-color: rgba(139, 92, 246, 0.4);
    background: rgba(139, 92, 246, 0.03);
  }

  [data-theme="light"] & {
    border-color: ${colors.lightGray};
    &:hover {
      border-color: ${colors.neonViolet};
      background: rgba(139, 92, 246, 0.02);
    }
  }
`;

const DropLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: rgba(255, 255, 255, 0.6);

  [data-theme="light"] & {
    color: ${colors.steelGray};
  }
`;

const DropHint = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.3);

  [data-theme="light"] & {
    color: ${colors.hintGray};
  }
`;

const FileErrorMsg = styled.div`
  font-size: 13px; color: #ef4444; margin-top: 8px; text-align: center;
`;

const FileSelected = styled.div`
  display: flex;
  align-items: center;
  gap: ${spacing.md}px;
  padding: 14px 20px;
  background: rgba(139, 92, 246, 0.06);
  border: 1px solid rgba(139, 92, 246, 0.15);
  border-radius: 10px;
  width: 100%;
`;

const FileName = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #ffffff;
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  [data-theme="light"] & {
    color: ${colors.slateCharcoal};
  }
`;

const FileSize = styled.span`
  font-size: 12px;
  color: rgba(255, 255, 255, 0.4);
  flex-shrink: 0;

  [data-theme="light"] & {
    color: ${colors.hintGray};
  }
`;

const RemoveFileBtn = styled.button`
  padding: 4px 10px;
  border: 1px solid rgba(239, 68, 68, 0.3);
  border-radius: 6px;
  background: transparent;
  color: #f87171;
  font-size: 11px;
  font-weight: 600;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.15s;

  &:hover {
    background: rgba(239, 68, 68, 0.1);
  }

  [data-theme="light"] & {
    border-color: rgba(220, 38, 38, 0.2);
    color: #dc2626;
    &:hover { background: rgba(220, 38, 38, 0.05); }
  }
`;

/* Error */
const ErrorBox = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px ${spacing.lg}px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.2);
  border-radius: 10px;
  color: #f87171;
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing.lg}px;

  [data-theme="light"] & {
    background: #fef2f2;
    border-color: #fecaca;
    color: #dc2626;
  }
`;

const WarningBox = styled.div`
  padding: 12px ${spacing.lg}px;
  background: rgba(234, 179, 8, 0.08);
  border: 1px solid rgba(234, 179, 8, 0.2);
  border-radius: 10px;
  color: #eab308;
  font-size: ${typography.fontSize.sm};
  margin-bottom: ${spacing.lg}px;
  display: flex;
  flex-direction: column;
  gap: 4px;

  [data-theme="light"] & {
    background: #fefce8;
    border-color: #fde68a;
    color: #a16207;
  }
`;

const ErrorIcon = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: rgba(239, 68, 68, 0.2);
  font-size: 11px;
  font-weight: 700;
  flex-shrink: 0;
`;

